import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { VoidCallback } from 'types/callbacks';
import { CommunityGridItem as GridItem } from 'types/community';
import { GridItemErrorBoundary } from '../shared/GridErrorBoundary';
import CommunityGridItem, { PlaceholderGridItem } from './CommunityGridItem';
import styles from './CommunityGrid.module.css';
import { insertIf, join } from 'lib/helpers';
import { RiveFilesContext } from 'contexts/riveFile/RiveFilesContext';

type Props = {
  gridItems?: GridItem[] | null;
  hasMoreItems?: boolean;
  onScrollNearEnd?: VoidCallback;
  smallerGrid?: boolean;
  isValidating: boolean;
  placeholderCount?: number;
};

export default function CommunityGrid({
  gridItems,
  hasMoreItems,
  onScrollNearEnd,
  isValidating,
  smallerGrid = false,
}: Props) {
  const observerContainer = useRef<HTMLDivElement>(null);
  const [placeholderCount, setPlaceholderCount] = useState<number>(1);

  const { openInRive } = useContext(RiveFilesContext);

  const gridContainerRef = useCallback((container: any) => {
    if (container) {
      const style = window.getComputedStyle(container);
      const colCount = style
        .getPropertyValue('grid-template-columns')
        .split(' ').length;

      const windowHeight = window.innerHeight;
      const rowHeight = parseInt(
        style.getPropertyValue('grid-template-rows').split(' ')[0]
      );

      // At a minimum have a single row
      const rows = Math.ceil(windowHeight / rowHeight) || 1;

      setPlaceholderCount(colCount * rows);
    }
  }, []);

  useEffect(() => {
    const callback: IntersectionObserverCallback = ([entry]) => {
      // Don't allow callback to trigger if we are "validating", i.e if there is
      // still a request in flight to fetch more items.
      if (!isValidating && hasMoreItems && entry.isIntersecting) {
        onScrollNearEnd && onScrollNearEnd();
      }
    };

    const options = {
      rootMargin: '250px',
    };

    const observer = new IntersectionObserver(callback, options);
    observer.observe(observerContainer.current!);

    return () => observer.disconnect();
  }, [hasMoreItems, isValidating]);

  function getItems(gridItems?: GridItem[] | null) {
    if (gridItems === undefined || gridItems === null) {
      return [...Array(placeholderCount).keys()].map((i) => (
        <PlaceholderGridItem key={i} />
      ));
    }

    return gridItems.map((gridItem) => (
      <GridItemErrorBoundary key={gridItem.communityRevisionId}>
        <CommunityGridItem item={gridItem} openInRiveFile={openInRive} />
      </GridItemErrorBoundary>
    ));
  }

  const items = getItems(gridItems);

  return (
    <div
      className={join([
        styles.CommunityGrid,
        ...insertIf(smallerGrid, styles.SmallerGrid),
      ])}
    >
      <ul ref={gridContainerRef}>{items}</ul>
      <div ref={observerContainer} className={styles.ObserverContainer} />
    </div>
  );
}
